import React, { useEffect, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import { AssetSubdir, assetUrl, listAssets, uploadAsset } from './scenario-api';
import { StudioLoader } from './StudioLoader';
import styles from './scenario-studio.module.css';

interface AssetPickerModalProps {
  scenarioId: string;
  value: string;
  subdir?: AssetSubdir;
  onSelect: (path: string) => void;
  onClose: () => void;
}

export const AssetPickerModal: React.FC<AssetPickerModalProps> = ({
  scenarioId,
  value,
  subdir = 'backgrounds',
  onSelect,
  onClose,
}) => {
  const { user } = useAuth();
  const [assets, setAssets] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [activeSubdir, setActiveSubdir] = useState<AssetSubdir>(subdir);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setActiveSubdir(subdir);
  }, [subdir]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const list = await listAssets(scenarioId);
      setAssets(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(console.error);
  }, [scenarioId, user]);

  const filtered = assets.filter((a) => {
    const normalized = a.replace(/\\/g, '/');
    const prefix = `assets/${activeSubdir}/`;
    const inSubdir = normalized.startsWith(prefix);
    const matchesText =
      !filter || normalized.toLowerCase().includes(filter.toLowerCase());
    return inSubdir && matchesText;
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const path = await uploadAsset(scenarioId, file, activeSubdir);
      await load();
      onSelect(path);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const busy = loading || uploading;

  return (
    <div className={styles.modalOverlay} onClick={busy ? undefined : onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHeader}>
          <h3>Sélectionner un asset</h3>
          <button type="button" className={styles.iconBtn} onClick={onClose} disabled={uploading}>×</button>
        </header>
        <div className={styles.modalBody}>
          <div className={styles.row}>
            <input
              className={styles.input}
              placeholder="Filtrer…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={busy}
            />
            <select
              className={styles.select}
              value={activeSubdir}
              onChange={(e) => setActiveSubdir(e.target.value as AssetSubdir)}
              disabled={busy}
              title="Dossier affiché et destination d'upload"
            >
              <option value="backgrounds">backgrounds</option>
              <option value="characters">characters</option>
              <option value="elements">elements</option>
              <option value="items">items</option>
            </select>
            <label className={`${styles.btnSecondary} ${uploading ? styles.btnLoading : ''}`}>
              {uploading ? (
                <span className={styles.loaderInline} style={{ padding: 0 }}>
                  <span className={styles.spinner} /> Upload…
                </span>
              ) : (
                'Uploader'
              )}
              <input type="file" accept="image/*" hidden onChange={handleUpload} disabled={busy} />
            </label>
          </div>
          <div className={styles.assetGrid} style={{ position: 'relative', minHeight: 120 }}>
            {loading && <StudioLoader label="Chargement des assets…" variant="overlay" />}
            {!loading && filtered.map((path) => (
              <button
                key={path}
                type="button"
                className={`${styles.assetCard} ${value === path ? styles.assetCardSelected : ''}`}
                onClick={() => { onSelect(path); onClose(); }}
              >
                <img src={assetUrl(scenarioId, path)} alt="" />
                <span>{path.replace('assets/', '')}</span>
              </button>
            ))}
            {!loading && filtered.length === 0 && (
              <p className={styles.hint}>
                Aucun asset dans « {activeSubdir} ». Uploadez une image ou changez de dossier.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface AssetFieldProps {
  scenarioId: string;
  label: string;
  value: string;
  subdir?: AssetSubdir;
  onChange: (path: string) => void;
}

export const AssetField: React.FC<AssetFieldProps> = ({
  scenarioId,
  label,
  value,
  subdir,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.field}>
      <label>{label}</label>
      <div className={styles.assetFieldRow}>
        <input
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="assets/…"
        />
        <button type="button" className={styles.btnSecondary} onClick={() => setOpen(true)}>
          Parcourir
        </button>
      </div>
      {value && (
        <img className={styles.assetThumb} src={assetUrl(scenarioId, value)} alt="" />
      )}
      {open && (
        <AssetPickerModal
          scenarioId={scenarioId}
          value={value}
          subdir={subdir}
          onSelect={onChange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};
